import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import './ManageProviders.css';

interface Provider {
  uid: string;
  email: string;
  name: string;
  phoneNumber?: string;
  phone?: string;
  role: string;
  status?: 'inactive' | 'pending_approval' | 'active';
  deposit?: number;
  totalEarnings?: number;
  createdAt: any;
  updatedAt?: any;
}

const ManageProviders: React.FC = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [updating, setUpdating] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 50;

  const statusOptions = [
    { value: 'all', label: 'Tất cả' },
    { value: 'inactive', label: 'Không hoạt động' },
    { value: 'pending_approval', label: 'Chờ phê duyệt' },
    { value: 'active', label: 'Hoạt động' }
  ];

  const fetchProviders = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Bắt đầu tải danh sách nhà cung cấp...');

      let providersQuery;
      let querySnapshot;

      try {
        // Xây dựng query theo status để giảm dữ liệu tải về
        const constraints: any[] = [where('role', '==', 'provider')];
        if (statusFilter !== 'all') {
          constraints.push(where('status', '==', statusFilter));
        }
        // Order nếu có index, fallback nếu không
        try {
          providersQuery = query(
            collection(db, 'users'),
            ...constraints,
            orderBy('createdAt', 'desc')
          );
          console.log('Thử query với orderBy...');
          querySnapshot = await getDocs(providersQuery);
        } catch (indexError) {
          console.warn('Query với orderBy thất bại, thử query không order:', indexError);
          providersQuery = query(
            collection(db, 'users'),
            ...constraints
          );
          console.log('Thử query đơn giản...');
          querySnapshot = await getDocs(providersQuery);
        }
      } catch (qErr) {
        console.warn('Xây dựng query có điều kiện thất bại, fallback lấy tất cả provider:', qErr);
        providersQuery = query(
          collection(db, 'users'),
          where('role', '==', 'provider')
        );
        querySnapshot = await getDocs(providersQuery);
      }

      const providersData: Provider[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Provider doc:', doc.id, data);
        
        providersData.push({
          uid: doc.id,
          email: data.email || '',
          name: data.name || data.displayName || '',
          phoneNumber: data.phoneNumber,
          phone: data.phone,
          role: data.role,
          status: data.status || 'pending_approval',
          deposit: data.deposit || 0,
          totalEarnings: data.totalEarnings || 0,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        });
      });

      // Sắp xếp thủ công nếu cần
      if (providersData.length > 0) {
        providersData.sort((a, b) => {
          if (!a.createdAt && !b.createdAt) return 0;
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          
          const aTime = a.createdAt?.seconds ? a.createdAt.seconds : 0;
          const bTime = b.createdAt?.seconds ? b.createdAt.seconds : 0;
          return bTime - aTime;
        });
      }

      console.log(`Đã tải ${providersData.length} nhà cung cấp`);
      setProviders(providersData);
      setFilteredProviders(providersData);
      
    } catch (error: any) {
      console.error('Lỗi chi tiết khi tải danh sách nhà cung cấp:', error);
      
      let errorMessage = 'Không thể tải danh sách nhà cung cấp';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Không có quyền truy cập dữ liệu. Vui lòng kiểm tra quyền đăng nhập.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Dịch vụ hiện không khả dụng. Vui lòng thử lại sau.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = 'Cần tạo index cho database. Vui lòng liên hệ quản trị viên.';
      } else if (error.message) {
        errorMessage = `Lỗi: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const normalizePhoneDigits = (value: string) => value.replace(/\D/g, '');

  const applyFilters = (providersToFilter: Provider[]) => {
    let filtered = providersToFilter;

    // Filter theo tên hoặc số điện thoại
    const q = nameFilter.trim();
    if (q) {
      const qLower = q.toLowerCase();
      const qDigits = normalizePhoneDigits(q);
      filtered = filtered.filter((provider) => {
        if (provider.name.toLowerCase().includes(qLower)) return true;
        const p1 = String(provider.phoneNumber ?? '');
        const p2 = String(provider.phone ?? '');
        const phoneCombined = `${p1} ${p2}`.trim().toLowerCase();
        if (phoneCombined && phoneCombined.includes(qLower)) return true;
        if (qDigits.length > 0) {
          const phoneDigits = normalizePhoneDigits(p1 + p2);
          if (phoneDigits.includes(qDigits)) return true;
        }
        return false;
      });
    }

    // Filter theo trạng thái
    if (statusFilter !== 'all') {
      filtered = filtered.filter(provider => provider.status === statusFilter);
    }

    return filtered;
  };

  const updateProviderStatus = async (providerId: string, newStatus: string) => {
    try {
      setUpdating(providerId);
      const providerRef = doc(db, 'users', providerId);
      await updateDoc(providerRef, {
        status: newStatus,
        updatedAt: new Date()
      });

      // Cập nhật state local
      const updatedProviders = providers.map(provider =>
        provider.uid === providerId
          ? { ...provider, status: newStatus as any }
          : provider
      );
      setProviders(updatedProviders);

      // Áp dụng lại filter
      const filtered = applyFilters(updatedProviders);
      setFilteredProviders(filtered);

    } catch (error) {
      console.error('Lỗi khi cập nhật tình trạng:', error);
      setError('Cập nhật tình trạng thất bại');
    } finally {
      setUpdating(null);
    }
  };

  const getStatusLabel = (status?: string) => {
    const statusMap: { [key: string]: string } = {
      'inactive': 'Không hoạt động',
      'pending_approval': 'Chờ phê duyệt',
      'active': 'Hoạt động'
    };
    return status ? statusMap[status] || status : 'Chưa cập nhật';
  };

  const getStatusClass = (status?: string) => {
    const statusClassMap: { [key: string]: string } = {
      'inactive': 'status-inactive',
      'pending_approval': 'status-pending',
      'active': 'status-active'
    };
    return status ? statusClassMap[status] || '' : 'status-unknown';
  };

  const getRowClass = (status?: string) => {
    if (!status) return '';
    const rowClassMap: { [key: string]: string } = {
      'inactive': 'row-inactive',
      'pending_approval': 'row-pending',
      'active': 'row-active'
    };
    return rowClassMap[status] || '';
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '0 VND';
    return amount.toLocaleString('vi-VN') + ' VND';
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Chưa cập nhật';
    
    try {
      let date: Date;
      
      if (timestamp.seconds) {
        // Firestore Timestamp
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp.toDate) {
        // Firestore Timestamp object
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        // JavaScript Date
        date = timestamp;
      } else {
        // String or number
        date = new Date(timestamp);
      }
      
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Lỗi định dạng';
    }
  };

  const getPhoneNumber = (provider: Provider) => {
    return provider.phoneNumber || provider.phone || 'Chưa cập nhật';
  };

  // Effect: fetch khi vào trang và khi statusFilter đổi (server-side filter)
  useEffect(() => {
    setCurrentPage(1);
    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Memoized filtered providers (client-side tên / SĐT + trạng thái)
  const memoFilteredProviders = useMemo(() => applyFilters(providers), [providers, nameFilter, statusFilter]);

  // Pagination derived state
  const totalPages = useMemo(() => Math.ceil(memoFilteredProviders.length / recordsPerPage) || 1, [memoFilteredProviders.length]);
  const displayedProviders = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return memoFilteredProviders.slice(start, start + recordsPerPage);
  }, [memoFilteredProviders, currentPage]);

  useEffect(() => {
    // Keep controlled filtered state for existing JSX usage
    setFilteredProviders(memoFilteredProviders);
    if (currentPage > totalPages) setCurrentPage(1);
  }, [memoFilteredProviders, currentPage, totalPages]);

  if (loading) {
    return (
      <div className="manage-providers">
        <div className="page-header">
          <h2>Quản lý nhà cung cấp</h2>
          <p className="page-subtitle">Quản lý và theo dõi tất cả nhà cung cấp dịch vụ</p>
        </div>
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải dữ liệu...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-providers">
      <div className="page-header">
        <h2>Quản lý nhà cung cấp</h2>
        <p className="page-subtitle">Quản lý và theo dõi tất cả nhà cung cấp dịch vụ</p>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="name-filter">Tìm kiếm:</label>
          <input
            id="name-filter"
            type="text"
            placeholder="Tên hoặc số điện thoại..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Tình trạng:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button onClick={fetchProviders} className="refresh-button">
          Làm mới
        </button>
      </div>

      {error && (
        <div className="error-message">
          <div>
            <strong>{error}</strong>
            <br />
            <small>💡 Mở Developer Console (F12) để xem thông tin chi tiết về lỗi</small>
          </div>
          <button 
            onClick={() => {
              setError(null);
              fetchProviders();
            }}
            className="retry-button"
          >
            Thử lại
          </button>
        </div>
      )}

      <div className="table-container">
        <table className="providers-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Tên</th>
              <th>Số điện thoại</th>
              <th>Email</th>
              <th>Tình trạng</th>
              <th>Số tiền ký quỹ</th>
              <th>Tổng thu nhập</th>
              <th>Ngày tạo</th>
            </tr>
          </thead>
          <tbody>
            {displayedProviders.length === 0 ? (
              <tr>
                <td colSpan={8} className="no-data">
                  {memoFilteredProviders.length === 0 ? 'Không có nhà cung cấp nào' : 'Không có dữ liệu cho trang này'}
                </td>
              </tr>
            ) : (
              displayedProviders.map((provider, index) => (
                <tr key={provider.uid} className={getRowClass(provider.status)}>
                  <td className="order-cell">
                    <span className="order-display">{(currentPage - 1) * recordsPerPage + index + 1}</span>
                  </td>
                  <td className="provider-name">
                    <button 
                      className="provider-name-link"
                      onClick={() => navigate(`/admin/providers/${provider.uid}`)}
                      title="Xem chi tiết nhà cung cấp"
                    >
                      {provider.name}
                    </button>
                  </td>
                  <td>{getPhoneNumber(provider)}</td>
                  <td>{provider.email}</td>
                  <td>
                    <span className={`status ${getStatusClass(provider.status)}`}>
                      {getStatusLabel(provider.status)}
                    </span>
                  </td>
                  <td className="currency-cell">{formatCurrency(provider.deposit)}</td>
                  <td className="currency-cell">{formatCurrency(provider.totalEarnings)}</td>
                  <td className="date-cell">{formatDate(provider.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Hiển thị {(currentPage - 1) * recordsPerPage + 1} - {Math.min(currentPage * recordsPerPage, memoFilteredProviders.length)} của {memoFilteredProviders.length} kết quả
          </div>
          <div className="pagination-controls">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="pagination-btn">««</button>
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="pagination-btn">«</button>
            {(() => {
              const buttons = [] as React.ReactElement[];
              const start = Math.max(1, currentPage - 2);
              const end = Math.min(totalPages, currentPage + 2);
              for (let i = start; i <= end; i++) {
                buttons.push(
                  <button key={i} onClick={() => setCurrentPage(i)} disabled={i === currentPage} className={`pagination-btn ${i === currentPage ? 'active' : ''}`}>{i}</button>
                );
              }
              return buttons;
            })()}
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="pagination-btn">»</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="pagination-btn">»»</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageProviders; 